import { DisplayableData, Entity, EntityId } from 'entities-next/common'
import { CriteriaLogic } from 'entities-next/criteriaConfig/entities'

export type InvestorCriteria = Entity & {
  investorId: EntityId
  checkLogic: CriteriaLogic
  checkType: CriteriaType
  checkData?:
    | NumericCriteriaData
    | SelectCriteriaData
    | TextCriteriaData
    | StageCriteriaData
    | boolean
    | AdvancedSelectCriteriaData
  relaxedSweetSpot: boolean
}

type InvestorCriteriaStrictData = {
  [CriteriaLogic.organizationFoundedAt]: OptionalDateRangeCheckData
  [CriteriaLogic.organizationStage]: StageCriteriaData
  [CriteriaLogic.organizationHeadquarters]: LocationCriteriaData
  [CriteriaLogic.negativeOrganizationHeadquarters]: LocationCriteriaData
}

export type StrictCriterialType = keyof InvestorCriteriaStrictData

export type StrictInvestorCriteria<Criteria extends CriteriaLogic & StrictCriterialType> = Omit<
  InvestorCriteria,
  'checkLogic' | 'checkData'
> & {
  checkLogic: Criteria
  checkData: InvestorCriteriaStrictData[Criteria]
}

export type WithInvestorCriteria = {
  checks: InvestorCriteria[]
}

export enum CriteriaType {
  criterion = 'criterion',
  signal = 'signal',
}

export enum CriteriaDataLogic {
  and = 'and',
  or = 'or',
}

export type NumericCriteriaData = {
  min?: number | string
  max?: number | string
}

export type SelectCriteriaDataContent<Entity = any> = DisplayableData & { value: Entity }

export type SelectCriteriaData = {
  content: SelectCriteriaDataContent[]
  dataLogic: CriteriaDataLogic
}

export type StageCriteriaData = SelectCriteriaData & {
  stages: string[]
}

export type Location = { countryCode: string } | { continent: string }

export type LocationCriteriaData = SelectCriteriaData & {
  locations: Location[]
}

export type TextCriteriaData = {
  content: { value: string }[]
  dataLogic: CriteriaDataLogic
}

/**
 * Why the date from and to is a string?
 * The check-data of investor checks only allows json, but this json are not parsed or validated, are only type
 * interface, for this reason the values are stored as string, and will be parsed in the evaluator.
 */
export type OptionalDateRangeCheckData = {
  minimumYear?: string
  maximumYear?: string
}

export type OptionalDateRange = {
  minimumYear?: Date
  maximumYear?: Date
}

export function optionalDateRangeCheckDataToOptionalDateRange({
  minimumYear,
  maximumYear,
}: OptionalDateRangeCheckData): OptionalDateRange {
  return {
    minimumYear: minimumYear ? new Date(minimumYear) : undefined,
    maximumYear: maximumYear ? new Date(maximumYear) : undefined,
  }
}

export type CriteriaDataWithLogic<Entity = DisplayableData> = {
  relation: CriteriaDataLogic
  between: Array<Entity | CriteriaDataWithLogic<Entity>>
}

export type AdvancedSelectCriteriaData<Entity = any> = CriteriaDataWithLogic<SelectCriteriaDataContent<Entity>>

export const INVESTOR_CRITERIA_CACHE_STORE_ID = 'investor-criteria'
